import React, { useEffect, useState } from "react";
import { Model } from "../../model_gen/model";
import { ModelService } from "../../services/ModelService";
import { Table, Typography, Spin, Empty, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import moment from "moment";

const { Title, Text } = Typography;

interface ModelTableProps {
  apiKey: string;
  applicationId?: number;
  intentGroupId?: number;
  intentId?: number;
}

const ModelTable: React.FC<ModelTableProps> = ({
  apiKey,
  applicationId,
  intentGroupId,
  intentId,
}) => {
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [sortedInfo, setSortedInfo] = useState<SorterResult<Model>>({
    columnKey: "model",
    order: "ascend",
  });

  useEffect(() => {
    let modelObservable;

    // Choose the appropriate service method based on provided props
    if (intentId) {
      modelObservable = ModelService.getModelsByIntent(
        intentId,
        setLoading,
        apiKey
      );
    } else if (intentGroupId) {
      modelObservable = ModelService.getModelsByIntentGroup(
        intentGroupId,
        setLoading,
        apiKey
      );
    } else if (applicationId) {
      modelObservable = ModelService.getModelsByApplication(
        applicationId,
        setLoading,
        apiKey
      );
    } else {
      modelObservable = ModelService.getModels(setLoading, apiKey);
    }

    const subscription = modelObservable.subscribe(
      (data: Model[]) => {
        // Filter to only include finetuned models
        const finetunedModels = data.filter((model) => model.finetuned);
        setModels(finetunedModels);
      },
      (error: any) => {
        console.error("Error fetching models:", error);
        setModels([]);
      }
    );

    return () => subscription.unsubscribe();
  }, [apiKey, applicationId, intentGroupId, intentId]);

  const columns: ColumnsType<Model> = [
    {
      title: "Model Name",
      dataIndex: "model",
      key: "model",
      width: "30%",
      sorter: (a, b) => {
        const modelA = (a.model || "").toLowerCase();
        const modelB = (b.model || "").toLowerCase();
        return modelA.localeCompare(modelB);
      },
      sortOrder: sortedInfo.columnKey === "model" ? sortedInfo.order : null,
      render: (text: string) => (
        <Typography.Text style={{ fontWeight: 500, color: "#1890ff" }}>
          {text}
        </Typography.Text>
      ),
    },
    {
      title: "Base Model",
      dataIndex: "base_model",
      key: "base_model",
      width: "25%",
      render: (text: string) => (
        <Typography.Text>{text || "-"}</Typography.Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "20%",
      render: (status: string | undefined) => {
        if (!status)
          return <Typography.Text type="secondary">-</Typography.Text>;

        let color = "default";
        switch (status.toLowerCase()) {
          case "active":
          case "enabled":
            color = "success";
            break;
          case "disabled":
            color = "error";
            break;
          case "pending":
            color = "processing";
            break;
          default:
            color = "default";
        }
        return <Tag color={color}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Date Created",
      dataIndex: "date_added",
      key: "date_added",
      width: "25%",
      render: (timestamp: number | undefined) => {
        if (!timestamp)
          return <Typography.Text type="secondary">-</Typography.Text>;
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const handleTableChange = (
    _pagination: any,
    _filters: any,
    sorter: SorterResult<Model> | SorterResult<Model>[]
  ) => {
    const sortInfo = Array.isArray(sorter) ? sorter[0] : sorter;
    setSortedInfo(sortInfo);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "40px 0",
        }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Title level={5} style={{ margin: "0 0 16px 0", fontWeight: 600 }}>
        Models
      </Title>
      <div style={{ borderRadius: "8px", overflow: "hidden" }}>
        <Table
          dataSource={models}
          columns={columns}
          rowKey="id"
          pagination={false}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <Empty
                description={
                  <div>
                    <Text
                      strong
                      style={{ display: "block", marginBottom: "8px" }}>
                      No fine-tuned models found
                    </Text>
                    <Text type="secondary">
                      No fine-tuned models have been created yet.
                    </Text>
                  </div>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>
    </div>
  );
};

export default ModelTable;
